import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useDashTheme } from "../../../hooks/useDashTheme";
import { storyVars } from "./storyBits";

/* Same chrome as DashboardV2 Drawer / DetailDrawer — one surface, one ink.
   Tone is not used on the shell. Category colour stays on the page cards. */

export function StoryDrawer({ open, onClose, kicker, title, chips = [], icon, crumbs = [], children }) {
  const t = useDashTheme();

  React.useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <React.Fragment>
          <motion.div
            key="scrim"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{
              position: "fixed", inset: 0, zIndex: 9998,
              background: "rgba(8,10,18,0.5)", backdropFilter: "blur(3px)",
            }}
          />
          <motion.aside
            key="panel"
            role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : "Details"}
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={t.motion.spring.soft}
            style={{
              position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 9999,
              width: "min(720px, 100vw)",
              display: "flex", flexDirection: "column",
              background: t.c.surface, color: t.c.text, fontFamily: t.font.family,
              borderLeft: `1px solid ${t.c.border}`,
              boxShadow: t.shadow.lg,
              ...storyVars(t),
            }}
          >
            <div style={{
              flex: "none",
              display: "flex", alignItems: "flex-start", justifyContent: "space-between",
              gap: 12, padding: `${t.s[5]}px ${t.s[5]}px ${t.s[4]}px`,
              borderBottom: `1px solid ${t.c.border}`,
              background: t.c.surface,
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, minWidth: 0, flex: 1 }}>
                {icon && (
                  <span style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    width: 40, height: 40, borderRadius: 11, flex: "none",
                    background: t.c.primarySoft, color: t.c.primary,
                  }}>{icon}</span>
                )}
                <div style={{ minWidth: 0, flex: 1 }}>
                  {(kicker || crumbs.length > 0) && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
                      {crumbs.map((c, i) => (
                        <React.Fragment key={i}>
                          <button
                            type="button" onClick={c.onClick}
                            className="st-reset st-focusable st-textlink tg-crumb"
                            title={`Back to ${c.label}`}
                            style={{
                              fontSize: 12, fontWeight: 600, color: t.c.textTertiary,
                              maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}
                          >{c.label}</button>
                          <span aria-hidden="true" style={{ color: t.c.textTertiary, fontSize: 12 }}>›</span>
                        </React.Fragment>
                      ))}
                      {kicker && (
                        <span style={{ ...t.font.statLabel, color: t.c.primary }}>{kicker}</span>
                      )}
                    </div>
                  )}
                  <div style={{
                    fontSize: t.font.size.lg, fontWeight: t.font.weight.semibold,
                    letterSpacing: "-0.015em", lineHeight: 1.3, color: t.c.text,
                    overflow: "hidden", textOverflow: "ellipsis",
                  }}>{title}</div>
                  {chips.filter(Boolean).length > 0 && (
                    <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                      {chips.filter(Boolean).map((c, i) => (
                        <DrawerChip key={i}>{c}</DrawerChip>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button" onClick={onClose} aria-label="Close"
                className="st-focusable st-hit-icon"
                style={{
                  width: 32, height: 32, borderRadius: 8, flex: "none",
                  border: "none", background: t.c.surfaceMuted, color: t.c.textSecondary,
                  cursor: "pointer", display: "grid", placeItems: "center",
                }}
              ><X size={16} /></button>
            </div>

            <div className="st-drawer-body" style={{
              flex: 1, overflowY: "auto", padding: t.s[5],
              fontSize: t.font.size.base, lineHeight: 1.65,
            }}>
              {children}
            </div>
          </motion.aside>
        </React.Fragment>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export function DrawerChip({ children, style }) {
  const t = useDashTheme();
  return (
    <span style={{
      fontSize: 12, fontWeight: 600, padding: "4px 10px",
      borderRadius: t.r.pill, background: t.c.surfaceMuted,
      border: `1px solid ${t.c.border}`, color: t.c.textSecondary,
      fontVariantNumeric: "tabular-nums", ...style,
    }}>{children}</span>
  );
}

export function DrawerSec({ title, children, style }) {
  const t = useDashTheme();
  return (
    <div style={{ marginBottom: 24, ...style }}>
      <div style={{ ...t.font.statLabel, color: t.c.primary, marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

export function DrawerSpine({ spans = [], bounds, startLabel, endLabel }) {
  const t = useDashTheme();
  const t0 = bounds?.startMs, t1 = bounds?.endMs;
  const span = Math.max(1, (t1 || 0) - (t0 || 0));
  return (
    <div>
      <div style={{
        position: "relative", height: 36, borderRadius: t.r.md, overflow: "hidden",
        background: t.c.surfaceMuted, border: `1px solid ${t.c.border}`,
      }}>
        {spans.map((s, i) => (
          <span key={i} style={{
            position: "absolute", top: 6, bottom: 6, borderRadius: 4, background: t.c.primary,
            left: `${Math.max(0, ((s.startMs - t0) / span) * 100)}%`,
            width: `${Math.max(0.8, ((s.endMs - s.startMs) / span) * 100)}%`,
          }} />
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
        <span style={{ fontSize: 12, color: t.c.textTertiary, fontWeight: 600 }}>{startLabel}</span>
        <span style={{ fontSize: 12, color: t.c.textTertiary, fontWeight: 600 }}>{endLabel}</span>
      </div>
    </div>
  );
}
